import React from 'react';

import { useUnreadCount } from './useUnreadCountRegistry';

const FAVICON_ELEMENT_ID = 'default-icon';
const BADGE_COLOR = '#EA4335';
const BADGE_RADIUS = 6;
const CANVAS_SIZE = 32;

// Stored as a DOM attribute so it survives re-mounts without being overwritten by a badge data URL.
const ORIGINAL_HREF_ATTR = 'data-original-favicon-href';

// Used when the original favicon can't be fetched (e.g. cross-origin without CORS).
const FALLBACK_BADGE =
    "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><circle cx='12' cy='4' r='3' fill='%23EA4335' stroke='white' stroke-width='1.5'/></svg>";

const getFaviconElement = (): HTMLLinkElement | null =>
    document.getElementById(FAVICON_ELEMENT_ID) as HTMLLinkElement | null;

const getOriginalHref = (linkElement: HTMLLinkElement): string | null => {
    const stored = linkElement.getAttribute(ORIGINAL_HREF_ATTR);
    if (stored) {
        return stored;
    }

    const hrefAttr = linkElement.getAttribute('href') || '';
    if (!hrefAttr) {
        return null;
    }

    // Data URLs are loaded directly; regular URLs need the resolved absolute URL for fetch.
    const originalHref = hrefAttr.startsWith('data:') ? hrefAttr : linkElement.href;
    linkElement.setAttribute(ORIGINAL_HREF_ATTR, originalHref);
    return originalHref;
};

const buildBadgeFavicon = async (originalHref: string): Promise<string> => {
    try {
        let imageSrc: string;
        let objectUrl: string | null = null;

        if (originalHref.startsWith('data:')) {
            // Data URLs can be loaded directly into an Image without a fetch round-trip.
            imageSrc = originalHref;
        } else {
            const response = await fetch(originalHref);
            if (!response.ok) {
                throw new Error('fetch failed');
            }
            const blob = await response.blob();
            objectUrl = URL.createObjectURL(blob);
            imageSrc = objectUrl;
        }

        try {
            const image = await new Promise<HTMLImageElement>((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.onerror = reject;
                img.src = imageSrc;
            });

            const canvas = document.createElement('canvas');
            canvas.width = CANVAS_SIZE;
            canvas.height = CANVAS_SIZE;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                throw new Error('no canvas context');
            }

            ctx.drawImage(image, 0, 0, CANVAS_SIZE, CANVAS_SIZE);

            const badgeX = CANVAS_SIZE - BADGE_RADIUS - 2;
            const badgeY = BADGE_RADIUS + 2;

            // White ring to make the dot visible against any favicon background
            ctx.beginPath();
            ctx.arc(badgeX, badgeY, BADGE_RADIUS + 1.5, 0, 2 * Math.PI);
            ctx.fillStyle = 'white';
            ctx.fill();

            ctx.beginPath();
            ctx.arc(badgeX, badgeY, BADGE_RADIUS, 0, 2 * Math.PI);
            ctx.fillStyle = BADGE_COLOR;
            ctx.fill();

            return canvas.toDataURL('image/png');
        } finally {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        }
    } catch {
        return FALLBACK_BADGE;
    }
};

/** Restores the original favicon when the count returns to zero or on unmount. */
export const useFaviconBadge = () => {
    const { totalCount } = useUnreadCount();

    React.useEffect(() => {
        let cancelled = false;

        const linkElement = getFaviconElement();
        const originalHref = linkElement ? getOriginalHref(linkElement) : null;

        if (linkElement && originalHref) {
            if (totalCount === 0) {
                linkElement.href = originalHref;
            } else if (linkElement.href === originalHref) {
                // Badge not yet applied — build and apply it.
                buildBadgeFavicon(originalHref).then((badgeHref) => {
                    if (!cancelled) {
                        const el = getFaviconElement();
                        if (el) {
                            el.href = badgeHref;
                        }
                    }
                });
            }
            // else: badge already showing (count-agnostic appearance) — skip rebuild.
        }

        return () => {
            cancelled = true;
        };
    }, [totalCount]);

    React.useEffect(
        () => () => {
            const linkElement = getFaviconElement();
            if (linkElement) {
                const originalHref = linkElement.getAttribute(ORIGINAL_HREF_ATTR);
                if (originalHref) {
                    linkElement.href = originalHref;
                    linkElement.removeAttribute(ORIGINAL_HREF_ATTR);
                }
            }
        },
        [],
    );
};
